import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Tab, Table, Tabs } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { useParams } from "react-router-dom";
import Nav from "../../components/breadcrumb";
import { ErrorLoading, PageLoading } from "../../components/loading";
import PageHeader, { ActionBar, ActionButton, Title } from "../../components/pageheader";
import { ConfirmAction } from "../../components/prompt";
import { errorAlert, infoAlert, successAlert } from "../../components/toastr";
import { deleteFeedbackQuestion, deleteRegistration, getEvent, updateEvent } from "../../resources/api/events";
import EventForm from "./event-form";
import QuestionForm from "./question-form";
import RegistrationForm from "./registration-form";

const bool = value => value === true || value === 1 || value === "1";

const PublicLink = ({ title, icon, url, active }) => {
    const qrCodeRef = useRef(null);

    const copy = () => {
        if (!window.navigator.clipboard) return infoAlert(url, `${title} link`);
        window.navigator.clipboard.writeText(url).then(() => successAlert(`${title} link copied.`)).catch(() => infoAlert(url, `${title} link`));
    };

    const download = () => {
        const canvas = qrCodeRef.current;
        if (!canvas) return errorAlert("Unable to prepare the QR code for download.");

        const fileName = title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        const link = document.createElement("a");
        link.download = `${fileName}-qr-code.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    return (
        <Card className="h-100 shadow-sm">
            <Card.Body>
                <div className="d-flex align-items-center mb-3">
                    <i className={`${icon} fa-2x text-success me-3`} />
                    <div className="flex-grow-1">
                        <h6 className="mb-1">{title}</h6>
                        {active !== undefined && <Badge bg={active ? "success" : "secondary"}>{active ? "Active" : "Closed"}</Badge>}
                    </div>
                </div>
                <div className="bg-light rounded p-3 mb-3 text-center">
                    <QRCodeCanvas
                        ref={qrCodeRef}
                        value={url}
                        size={256}
                        level="M"
                        marginSize={4}
                        title={`${title} QR code`}
                        style={{ maxWidth: "100%", height: "auto" }}
                    />
                </div>
                <div className="small text-break text-muted">{url}</div>
                <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button variant="outline-secondary" size="sm" onClick={copy}>
                        <i className="far fa-copy me-1" />Copy link
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={download}>
                        <i className="fas fa-download me-1" />Download QR code
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

const EventDetails = () => {
    const { eventid } = useParams();
    const [details, setDetails] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [eventForm, setEventForm] = useState(false);
    const [registrationForm, setRegistrationForm] = useState(false);
    const [questionForm, setQuestionForm] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [search, setSearch] = useState("");
    const [changingRegistration, setChangingRegistration] = useState(false);
    const [changingFeedback, setChangingFeedback] = useState(false);

    useEffect(() => {
        getEvent(eventid, ["registrations", "feedback_questions", "feedback_submissions"])
            .then(({ event }) => setDetails({
                ...event,
                registrations: event.registrations || [],
                feedback_questions: event.feedback_questions || event.questions || [],
                feedback_submissions: event.feedback_submissions || event.submissions || []
            }))
            .catch(setError)
            .finally(() => setLoaded(true));
    }, [eventid]);

    const registrations = useMemo(() => {
        if (!details) return [];
        const value = search.trim().toLowerCase();
        if (!value) return details.registrations;
        return details.registrations.filter(item => [item.full_name, item.email, item.phone, item.organisation]
            .some(field => String(field || "").toLowerCase().includes(value)));
    }, [details, search]);

    if (!loaded) return <PageLoading>Loading event...</PageLoading>;
    if (error) return <ErrorLoading>{error}</ErrorLoading>;
    if (!details) return <ErrorLoading>Event not found.</ErrorLoading>;

    const landingUrl = `${window.location.origin}/events/${details.short_code}`;
    const registrationUrl = `${window.location.origin}/events/${details.short_code}/registrations`;
    const feedbackUrl = `${window.location.origin}/events/${details.short_code}/feedback`;
    const questionsLocked = bool(details.feedbackStarted);
    const feedbackResponses = (details.feedback_responses || []).concat(
        details.feedback_submissions.flatMap(submission => submission.responses || [])
    );
    const responseValue = response => typeof response === "object" ? response.response : response;
    const getQuestionResponses = question => {
        if (Array.isArray(question.responses)) return question.responses;
        return feedbackResponses.filter(response => response.question_id === question.id);
    };
    const getAverage = question => {
        if (question.average_rating != null) return Number(question.average_rating);
        const ratings = getQuestionResponses(question).map(item => Number(responseValue(item))).filter(Number.isFinite);
        return ratings.length ? ratings.reduce((total, value) => total + value, 0) / ratings.length : null;
    };

    const saveEvent = event => setDetails(current => ({ ...current, ...event }));
    const saveRegistration = registration => setDetails(current => ({ ...current, registrations: [registration, ...current.registrations] }));
    const removeRegistration = id => {
        infoAlert("Deleting registration...");
        deleteRegistration(details.id, id)
            .then(({ message }) => {
                successAlert(message);
                setDetails(current => ({ ...current, registrations: current.registrations.filter(item => item.id !== id) }));
            }).catch(errorAlert);
    };
    const editQuestion = question => { setSelectedQuestion(question); setQuestionForm(true); };
    const newQuestion = () => { setSelectedQuestion(null); setQuestionForm(true); };
    const saveQuestion = question => setDetails(current => {
        const questions = [...current.feedback_questions];
        const index = questions.findIndex(item => item.id === question.id);
        if (index === -1) questions.push(question); else questions.splice(index, 1, question);
        return { ...current, feedback_questions: questions };
    });
    const removeQuestion = id => {
        infoAlert("Deleting feedback question...");
        deleteFeedbackQuestion(details.id, id)
            .then(({ message }) => {
                successAlert(message);
                setDetails(current => ({ ...current, feedback_questions: current.feedback_questions.filter(item => item.id !== id) }));
            }).catch(errorAlert);
    };
    const toggleRegistration = () => {
        const opening = !bool(details.registrationOpen);
        setChangingRegistration(true);
        updateEvent({ registrationOpen: opening }, details.id)
            .then(({ event, message }) => {
                successAlert(message);
                setDetails(current => ({ ...current, ...event, registrationOpen: opening }));
            })
            .catch(errorAlert)
            .finally(() => setChangingRegistration(false));
    };
    const toggleFeedback = () => {
        const opening = !bool(details.feedbackOpen);
        setChangingFeedback(true);
        updateEvent({ feedbackOpen: opening }, details.id)
            .then(({ event, message }) => {
                successAlert(message);
                setDetails(current => ({ ...current, ...event, feedbackOpen: opening, feedbackStarted: opening ? true : current.feedbackStarted }));
            })
            .catch(errorAlert)
            .finally(() => setChangingFeedback(false));
    };

    return (
        <>
            <Nav items={[{ title: "Events", href: "/app/events" }, { title: details.title }]} />
            <PageHeader maxWidth="1000">
                <Title>{details.title}</Title>
                <ActionBar>
                    <ActionButton onClick={() => setEventForm(true)} title="Edit Event"><i className="fas fa-pencil-alt" /></ActionButton>
                </ActionBar>
            </PageHeader>
            <div className="max-1000">
                <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">
                    <ConfirmAction
                        confirmationText={bool(details.registrationOpen) ? "Close public registration for this event?" : "Open public registration for this event?"}
                        handleAction={toggleRegistration}
                        disabled={changingRegistration}
                        size="sm"
                        variant={bool(details.registrationOpen) ? "outline-danger" : "success"}
                    >
                        {changingRegistration && <i className="fas fa-circle-notch fa-spin me-2" />}
                        <i className={`fas ${bool(details.registrationOpen) ? "fa-lock" : "fa-lock-open"} me-1`} />
                        {bool(details.registrationOpen) ? "Close Registration" : "Open Registration"}
                    </ConfirmAction>
                    <ConfirmAction
                        confirmationText={bool(details.feedbackOpen) ? "Close public feedback submissions? Questions will remain locked." : "Open public feedback now? Questions will be permanently locked."}
                        handleAction={toggleFeedback}
                        disabled={changingFeedback || (!bool(details.feedbackOpen) && details.feedback_questions.length === 0)}
                        size="sm"
                        variant={bool(details.feedbackOpen) ? "outline-danger" : "success"}
                        title={!bool(details.feedbackOpen) && details.feedback_questions.length === 0 ? "Add at least one feedback question first" : undefined}
                    >
                        {changingFeedback && <i className="fas fa-circle-notch fa-spin me-2" />}
                        <i className={`fas ${bool(details.feedbackOpen) ? "fa-comment-slash" : "fa-comment-dots"} me-1`} />
                        {bool(details.feedbackOpen) ? "Close Feedback" : "Open Feedback"}
                    </ConfirmAction>
                </div>
                <Tabs defaultActiveKey="overview" className="mb-4">
                    <Tab eventKey="overview" title="Overview">
                        <Row className="g-3 mb-4">
                            <Col md={7}>
                                <Card className="h-100"><Card.Body>
                                    <h5>Event Details</h5>
                                    <dl className="row mb-0">
                                        <dt className="col-sm-4">Description</dt><dd className="col-sm-8">{details.description || "n/a"}</dd>
                                        <dt className="col-sm-4">Location</dt><dd className="col-sm-8">{details.location || "n/a"}</dd>
                                        <dt className="col-sm-4">Date</dt><dd className="col-sm-8">{moment(details.start_date).format("DD MMM YYYY")}{details.end_date && details.end_date !== details.start_date ? ` – ${moment(details.end_date).format("DD MMM YYYY")}` : ""}</dd>
                                        <dt className="col-sm-4">Time</dt><dd className="col-sm-8">{details.start_time || "n/a"}{details.end_time ? ` – ${details.end_time}` : ""}</dd>
                                        <dt className="col-sm-4">Short Code</dt><dd className="col-sm-8"><code>{details.short_code}</code></dd>
                                        <dt className="col-sm-4">Status</dt><dd className="col-sm-8 text-capitalize">{details.status}</dd>
                                    </dl>
                                </Card.Body></Card>
                            </Col>
                            <Col md={5}>
                                <Card className="h-100"><Card.Body>
                                    <h5>Activity</h5>
                                    <Row className="text-center g-2 mt-1">
                                        <Col><div className="display-6">{details.registrations.length}</div><div className="small text-muted">Registrations</div></Col>
                                        <Col><div className="display-6">{details.feedback_submissions.length}</div><div className="small text-muted">Feedback</div></Col>
                                    </Row>
                                </Card.Body></Card>
                            </Col>
                            <Col md={4}><PublicLink title="View Event Details" icon="far fa-calendar" url={landingUrl} /></Col>
                            <Col md={4}><PublicLink title="Registration QR destination" icon="fas fa-qrcode" url={registrationUrl} active={bool(details.registrationOpen)} /></Col>
                            <Col md={4}><PublicLink title="Feedback QR destination" icon="fas fa-comment-dots" url={feedbackUrl} active={bool(details.feedbackOpen)} /></Col>
                        </Row>
                    </Tab>
                    <Tab eventKey="registrations" title={`Registrations (${details.registrations.length})`}>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                            <Form.Control size="sm" name="search" style={{ maxWidth: 320 }} placeholder="Search registrations" value={search} onChange={e => setSearch(e.currentTarget.value)} />
                            <Button size="sm" variant="success" onClick={() => setRegistrationForm(true)}><i className="fas fa-user-plus me-1" />Add Registration</Button>
                        </div>
                        {registrations.length ? <Table responsive hover style={{ minWidth: 760 }}><thead><tr><th>Name</th><th>Email</th><th>Telephone</th><th>Organisation</th><th>Method</th><th>Date</th><th /></tr></thead><tbody>{registrations.map(item => <tr key={item.id}>
                            <td>{item.full_name}</td><td>{item.email || "n/a"}</td><td>{item.phone || item.telephone || "n/a"}</td><td>{item.organisation || "n/a"}</td><td className="text-capitalize">{item.registration_method || "self"}</td><td>{item.date_added ? moment(item.date_added).format("DD MMM YYYY HH:mm") : "n/a"}</td>
                            <td className="text-end"><ConfirmAction as="button" className="btn btn-sm btn-link text-danger" confirmationText="Delete this registration?" handleAction={() => removeRegistration(item.id)}><i className="fas fa-trash-alt" /></ConfirmAction></td>
                        </tr>)}</tbody></Table> : <Alert variant="light" className="text-center text-muted">No registrations found.</Alert>}
                    </Tab>
                    <Tab eventKey="questions" title={`Questions (${details.feedback_questions.length})`}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>{questionsLocked && <Badge bg="secondary"><i className="fas fa-lock me-1" />Questions locked</Badge>}</div>
                            {!questionsLocked && <Button size="sm" variant="success" onClick={newQuestion}><i className="fas fa-plus-circle me-1" />Add Question</Button>}
                        </div>
                        {questionsLocked && <Alert variant="info">Questions are permanently locked because feedback has already opened for this event.</Alert>}
                        {details.feedback_questions.length ? <Table responsive hover><thead><tr><th>#</th><th>Question</th><th>Type</th>{!questionsLocked && <th />}</tr></thead><tbody>{details.feedback_questions.map((item, index) => <tr key={item.id}>
                            <td>{index + 1}</td><td>{item.question}</td><td>{item.question_type === "rating" ? "Rating (1–5)" : item.question_type === "options" ? `Options${bool(item.allow_other) ? " + Other" : ""}` : "Text response"}</td>
                            {!questionsLocked && <td className="text-end text-nowrap"><Button variant="link" size="sm" onClick={() => editQuestion(item)}><i className="fas fa-pencil-alt" /></Button><ConfirmAction as="button" className="btn btn-sm btn-link text-danger" confirmationText="Delete this question?" handleAction={() => removeQuestion(item.id)}><i className="fas fa-trash-alt" /></ConfirmAction></td>}
                        </tr>)}</tbody></Table> : <Alert variant="light" className="text-center text-muted">No feedback questions have been added.</Alert>}
                    </Tab>
                    <Tab eventKey="results" title={`Feedback Results (${details.feedback_submissions.length})`}>
                        <Row className="g-3">
                            <Col sm={4}><Card body className="text-center"><div className="display-5">{details.feedback_submissions.length}</div><div className="text-muted">Anonymous submissions</div></Card></Col>
                            <Col sm={8}><Alert variant="light" className="h-100 mb-0 d-flex align-items-center"><span><i className="fas fa-user-shield me-2" />Feedback is anonymous and is never linked to registration details.</span></Alert></Col>
                            {details.feedback_questions.filter(q => q.question_type === "rating").map(question => {
                                const average = getAverage(question);
                                return <Col md={6} key={question.id}><Card body className="h-100"><div className="small text-muted">{question.question}</div><div className="h3 mb-0">{average == null ? "—" : average.toFixed(1)} <small className="text-muted">/ 5</small></div></Card></Col>;
                            })}
                            {details.feedback_questions.filter(q => q.question_type === "textarea").map(question => {
                                const answers = getQuestionResponses(question);
                                return <Col xs={12} key={question.id}><Card><Card.Header className="fw-bold">{question.question}</Card.Header><Card.Body>
                                    {answers.length ? answers.map((answer, index) => <blockquote className="border-start border-3 ps-3 mb-3" key={answer.id || index}>{responseValue(answer)}</blockquote>) : <span className="text-muted">No text responses yet.</span>}
                                </Card.Body></Card></Col>;
                            })}
                            {details.feedback_questions.filter(q => q.question_type === "options").map(question => {
                                const answers = getQuestionResponses(question);
                                const total = answers.length;
                                const otherAnswers = answers.filter(answer => !answer.option_id);
                                return <Col md={6} key={question.id}><Card className="h-100"><Card.Header className="fw-bold">{question.question}</Card.Header><Card.Body>
                                    {(question.options || []).map(option => {
                                        const count = answers.filter(answer => answer.option_id === option.id).length;
                                        return <div className="d-flex justify-content-between border-bottom py-2" key={option.id}><span>{option.label}</span><strong>{count} <small className="text-muted">({total ? Math.round(count / total * 100) : 0}%)</small></strong></div>;
                                    })}
                                    {bool(question.allow_other) && <div className="d-flex justify-content-between py-2"><span>Other</span><strong>{otherAnswers.length} <small className="text-muted">({total ? Math.round(otherAnswers.length / total * 100) : 0}%)</small></strong></div>}
                                    {otherAnswers.length > 0 && <div className="mt-3 small">{otherAnswers.map((answer, index) => <blockquote className="border-start border-3 ps-2 mb-2" key={answer.id || index}>{responseValue(answer)}</blockquote>)}</div>}
                                </Card.Body></Card></Col>;
                            })}
                        </Row>
                    </Tab>
                </Tabs>
            </div>
            <EventForm show={eventForm} setShow={setEventForm} event={details} onSave={saveEvent} />
            <RegistrationForm eventId={details.id} show={registrationForm} setShow={setRegistrationForm} onSave={saveRegistration} />
            <QuestionForm eventId={details.id} show={questionForm} setShow={setQuestionForm} question={selectedQuestion} onSave={saveQuestion} />
        </>
    );
};

export default EventDetails;
